import pLimit from 'p-limit';

import * as Metrics from 'metrics/Metrics';
import * as Logging from 'util/Logging';
import {withExponentialBackoff} from 'util/requestWithExponentialBackoff';

import type {
  ActionableGoogleMimeBehavior,
  DirectDownloadOutputFileInfo,
  DownloadResult,
  DownloadSingleGoogleDriveFileInstructions,
  ExportOutputFileInfo,
  GoogleDriveIssue,
  GoogleMimeTypeBehavior,
  InputFileInfo,
} from './ValidateAndDownloadGoogleDriveFilesTypes';
import {
  createInvalidFilePickedDownloadResult,
  createUnableToDirectDownloadFileIssue,
  createUnableToExportFileIssue,
  GoogleDriveUnreachableIssue,
} from './ValidateAndDownloadGoogleDriveFilesTypes';

const GOOGLE_MIME_TYPE_PREFIX = 'application/vnd.google';
const GOOGLE_DRIVE_DOWNLOAD_FAILURE_METRIC = 'notebook.gdrive_picker.redirect.download.download_failure';
// Numerator/denominator pair used to alert on the rate at which the Picker API script
// is missing when validateAndDownloadGoogleDriveFiles is invoked. Emitted as separate
// metrics (instead of tags on a shared counter) so the alerting query can compute
// picker_api_script_not_loaded / check_if_picker_api_script_is_loaded directly.
const GOOGLE_DRIVE_CHECK_IF_PICKER_API_SCRIPT_IS_LOADED_METRIC =
  'notebook.gdrive_picker.redirect.download.check_if_picker_api_script_is_loaded';
const GOOGLE_DRIVE_PICKER_API_SCRIPT_NOT_LOADED_METRIC =
  'notebook.gdrive_picker.redirect.download.picker_api_script_not_loaded';

// Denominator for the download-failure alert + dashboard: download_failure /
// total_files_attempted_for_download.
//
// Emitted incrementally — one count per documentInfo, at the moment that file is actually processed:
// immediately before an invalid-file yield, or immediately after its retrieval task is
// queued. This mirrors the numerator (download_failure), which is likewise
// emitted one-per-event, so both halves of the ratio have the same shape.
//
// Emitting at *queue* time rather than delivered-result time is what keeps the
// ratio bounded: a p-limit task keeps running after the generator aborts and
// can still emit download_failure, so counting it when queued guarantees its
// potential numerator is already covered by the denominator. Counting only
// delivered results would let a late numerator exceed the denominator and push
// the failure rate above 100%.
//
// Emitting per processed file rather than upfront over documentInfos.length
// avoids the opposite skew: the loop below yields for invalid files, so a
// consumer that breaks on such a yield closes the generator before the
// remaining documents are ever validated or queued. Those files were never
// attempted, and counting them would inflate the denominator and under-report
// the failure rate.
//
// Emitting inline rather than accumulating into a single `finally` emission
// avoids depending on the generator being closed at all: a caller that drives
// it with manual .next() calls and then abandons it would never run a
// `finally`, leaving numerators with no denominator. There is a performance tradeoff
// here though; since we aren't batching, all of the individual metric calls will increase
// local metric queue size + server work.
const GOOGLE_DRIVE_TOTAL_FILES_ATTEMPTED_FOR_DOWNLOAD_METRIC =
  'notebook.gdrive_picker.redirect.download.total_files_attempted_for_download';

// Closed set of failure reasons recorded on the failure metric + log. Keep this a
// finite union (not `string`) so future edits can't accidentally record a
// high-cardinality value (e.g. a raw statusText or error message) as the `reason`
// metric tag or log dimension.
//
// Note: `unhandled_error` (a broken-assumption path — an unexpected exception
// surfaced out of downloadGoogleDriveFile) is recorded via an inline emission in
// downloadGoogleDriveFile's catch, NOT through logDownloadFailure. That inline
// path hands the ORIGINAL Error to Logging.error so Sentry captures its real
// message + stack; routing through logDownloadFailure would force the payload
// through a string message, and util/Logging.log would then wrap it in a fresh
// Error rooted inside Logging.log itself — discarding the original stack. The
// picker_api_script_not_loaded case is inline for the same reason (needs its
// own distinct metric + Logging.error payload).
type DownloadFailureReason =
  | 'invalid_file_missing_field'
  | 'invalid_file_unrecognized_or_unallowed_type'
  | 'unreachable'
  | 'http_error'
  | 'unhandled_error';

// The maximum number of download/export requests we want to wait for in parallel. We are capping this to reduce the number of concurrent requests to Google (to avoid things like rate limiting)
export const CONCURRENCY_LIMIT = 3;

const GOOGLE_MIME_TYPES: Record<string, GoogleMimeTypeBehavior> = {
  // Not allowed

  // Third party shortcut
  'application/vnd.google-apps.drive-sdk': {action: 'not_allowed'},
  // Google Form
  'application/vnd.google-apps.form': {action: 'not_allowed'},
  // Google Fusion Tables
  'application/vnd.google-apps.fusiontable': {action: 'not_allowed'},
  // Gmail layout template
  'application/vnd.google-apps.mail-layout': {action: 'not_allowed'},
  // Google My Maps
  'application/vnd.google-apps.map': {action: 'not_allowed'},
  // Google Apps Script
  'application/vnd.google-apps.script': {action: 'not_allowed'},
  // Drive shortcut
  'application/vnd.google-apps.shortcut': {action: 'not_allowed'},
  // Google Sites
  'application/vnd.google-apps.site': {action: 'not_allowed'},
  // Google Vids -- a video project in the Google Vids video editor
  'application/vnd.google-apps.vid': {action: 'not_allowed'},

  // Direct download -- some of these may not be downloadable; if the download fails, we'll return an error to the user

  // Google internal pointer tag for audio -- most likely can't download this, but we will attempt to
  'application/vnd.google-apps.audio': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Google tag for generic file -- may be able to download this, may not
  'application/vnd.google-apps.file': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Google internal pointer tag for photo -- most likely can't download this, but we will attempt to
  'application/vnd.google-apps.photo': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Google internal pointer tag for video -- most likely can't download this, but we will attempt to
  'application/vnd.google-apps.video': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Gemini Gem -- a template for Gemini conversations
  'application/vnd.google-gemini.gem': {action: 'direct', outMime: 'application/json', extension: '.json'},
  // Unknown type -- may be able to download this, may not
  'application/vnd.google-apps.unknown': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },

  // Export

  // Google Docs exports to PDF
  'application/vnd.google-apps.document': {action: 'export', outMime: 'application/pdf', extension: '.pdf'},
  // Google Drawing exports to PDF
  'application/vnd.google-apps.drawing': {action: 'export', outMime: 'application/pdf', extension: '.pdf'},
  // Google Slides exports to .pptx
  'application/vnd.google-apps.presentation': {
    action: 'export',
    outMime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    extension: '.pptx',
  },
  // Google Sheets exports to .xlsx
  'application/vnd.google-apps.spreadsheet': {
    action: 'export',
    outMime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: '.xlsx',
  },
  // Google Jamboard exports to PDF -- most likely users won't even have a jamboard file because it's deprecated
  'application/vnd.google-apps.jam': {action: 'export', outMime: 'application/pdf', extension: '.pdf'},
};

function getActionableGoogleMimeBehavior(mimeType: string): ActionableGoogleMimeBehavior | undefined {
  if (!Object.hasOwn(GOOGLE_MIME_TYPES, mimeType)) {
    return undefined;
  }
  const behavior = GOOGLE_MIME_TYPES[mimeType];
  if (behavior.action === 'not_allowed') {
    return undefined;
  }
  return behavior;
}

function logDownloadFailure(
  message: string,
  tags: {reason: DownloadFailureReason; action?: DownloadSingleGoogleDriveFileInstructions['action']},
  extra: Record<string, unknown> = {}
): void {
  // Tag the counter with `reason` so the dashboard can break `download_failure /
  // total_files_attempted_for_download` down by reason. Only `reason` is tagged —
  // it's drawn from a closed union (DownloadFailureReason), so its cardinality is
  // bounded. Higher-cardinality dimensions (`action`, `status`, `statusText`,
  // `googleError...`) stay in the warn log below, which is cheaper than tagging
  // and is where an on-call would look after the metric fires.
  Metrics.count(GOOGLE_DRIVE_DOWNLOAD_FAILURE_METRIC, 1, {reason: tags.reason});
  // Every reason routed through this helper is transient or expected (invalid
  // file picked, Google unreachable, non-OK HTTP), so log at warn to avoid
  // flooding Sentry with noise we can't act on. Broken-assumption cases
  // (`picker_api_script_not_loaded` and `unhandled_error`) are handled inline
  // by their respective call sites so they can (a) log at error level and
  // (b) hand the original Error to Logging.error so Sentry keeps its real
  // message + stack rather than a synthetic Error rooted in util/Logging.
  //
  // Fold the reason/action dimensions into the log payload so call sites don't
  // restate them in `extra`; call-site `extra` wins on any key collision.
  Logging.warn(message, {extra: {...tags, ...extra}});
}

export class GooglePickerAPIScriptNotLoadedError extends Error {
  constructor() {
    super('Google Picker API script must be loaded before calling validateAndDownloadGoogleDriveFiles');
    this.name = 'GooglePickerAPIScriptNotLoadedError';
  }
}

/**
 * Generates output filename for direct download of Google mime-type files.
 * Keeps the base of inputFileName the same, but if an extension already exists, overwrite it with outputFileExtension (otherwise just add outputFileExtension)
 *
 * Note: we are assuming that whatever stuff follows the last period in the filename is the original file extension, and
 * we overwrite that with the new file extension. This may not always be true--for example, a file could be named "doc.v2", in
 * which case we strip the v2 and replace it with the new file extension. This is a tradeoff we purposefully made to avoid having
 * to import the mime-types library to check if there is a valid file extension in the original name.
 */
export function generateOutputFilenameForDirectDownloadOfGoogleMimeFiles(
  inputFileName: string,
  outputFileExtension: string
): string {
  return inputFileName.includes('.')
    ? inputFileName.replace(/\.[^.]+$/, outputFileExtension)
    : inputFileName + outputFileExtension;
}

export async function downloadGoogleDriveFile(
  accessToken: string,
  instructions: DownloadSingleGoogleDriveFileInstructions
): Promise<DownloadResult> {
  // Wrap the whole body so any unexpected exception (e.g. a non-Abort/non-Type
  // error from fetch that withExponentialBackoff re-throws, or an unforeseen
  // failure in response.blob()/createUnable*Issue) increments the
  // download_failure counter with reason=`unhandled_error` before propagating.
  // Without this, the caller's denominator (total_files_attempted_for_download,
  // emitted when this file's retrieval task is queued) would still count this file
  // while the numerator (download_failure) would not — producing an artificially
  // low failure rate. Re-throw preserves the existing contract that unexpected
  // errors surface to the caller rather than being swallowed as a DownloadResult.
  try {
    // Percent-encode the two caller-supplied values interpolated into the Google API
    // URL so URL-significant characters (?, &, #, /, spaces, ...) are treated as data,
    // not as path/query syntax. `inputFileId` lands in the path and `outputFileMimeType`
    // in the query string; without encoding a value like `foo?alt=json` could tamper
    // with the request shape.
    //
    // Encoding alone is sufficient here — we deliberately do NOT add a regex/allow-list
    // validation of the fileId. Neither interpolated value is free user input:
    //   - `inputFileId` (the only Picker-supplied value that reaches the URL) is the id
    //     from the trusted Google Picker `DocumentObject`, null-checked in
    //     validateAndDownloadGoogleDriveFiles before this function is ever reached.
    //   - `outputFileMimeType` only enters the URL on the export path, and there it is
    //     drawn from our own GOOGLE_MIME_TYPES allow-list, not from the Picker. On the
    //     direct-download path the mimeType never enters the URL at all.
    // A syntactic whitelist would add no security value over encoding and risks
    // rejecting legitimate Google ids.
    const googleApiUrl =
      instructions.action === 'direct'
        ? `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(instructions.inputFileInfo.inputFileId)}?alt=media`
        : `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(instructions.inputFileInfo.inputFileId)}/export?mimeType=${encodeURIComponent(instructions.outputFileInfo.outputFileMimeType)}`;

    const {response, error} = await withExponentialBackoff<GoogleDriveIssue>(
      () =>
        fetch(googleApiUrl, {
          headers: {Authorization: `Bearer ${accessToken}`},
        }),
      {
        errorWhenRetriesExhausted: GoogleDriveUnreachableIssue,
      }
    );

    if (error != null) {
      logDownloadFailure(
        'GDrive download failed — Google Drive unreachable',
        {reason: 'unreachable', action: instructions.action},
        {retriesExhausted: true}
      );
      return {fileId: instructions.inputFileInfo.inputFileId, file: null, error};
    }

    // If there was a non-retryable http error code exporting the file, return an error
    if (!response.ok) {
      const issue =
        instructions.action === 'direct'
          ? await createUnableToDirectDownloadFileIssue(instructions.inputFileInfo, response)
          : await createUnableToExportFileIssue(
              instructions.inputFileInfo,
              instructions.outputFileInfo,
              response
            );

      logDownloadFailure(
        'GDrive download failed — non-OK HTTP response',
        {reason: 'http_error', action: instructions.action},
        {
          // The exact numeric status is logged here, so slicing 4xx vs 5xx (or any
          // specific code) is a log query — no need for separate coarse reasons.
          status: response.status,
          statusText: response.statusText,
          // Google's freeform error *message* can embed a file id / email on some 4xx
          // (403/404), so it's only safe to log on the generic 400. The structured
          // *reason* enum (googleErrorReason) never embeds identifiers, so log it on
          // every status — it's what disambiguates a 4xx sub-case (permission vs
          // rate-limit vs not-exportable) that a bare status code can't.
          ...(response.status === 400 ? {googleError: issue.googleErrorMessage} : {}),
          ...(issue.googleErrorReason != null ? {googleErrorReason: issue.googleErrorReason} : {}),
          // On the export path, include the input/output mimeTypes (both from our own
          // GOOGLE_MIME_TYPES allow-list, so no PII) so on-call can see which file-type
          // conversion failed alongside reason/action/status.
          ...(instructions.action === 'export'
            ? {
                inputFileMimeType: instructions.inputFileInfo.inputFileMimeType,
                outputFileMimeType: instructions.outputFileInfo.outputFileMimeType,
              }
            : {}),
        }
      );

      return {
        fileId: instructions.inputFileInfo.inputFileId,
        file: null,
        error: issue,
      };
    }

    const blob = await response.blob();

    return {
      fileId: instructions.inputFileInfo.inputFileId,
      file: new File([blob], instructions.outputFileInfo.outputFileName, {
        type: instructions.outputFileInfo.outputFileMimeType,
      }),
      error: null,
    };
  } catch (unhandledError) {
    // Emit the numerator inline (bypassing logDownloadFailure) so we can hand
    // the ORIGINAL Error to Logging.error: util/Logging.log passes an Error
    // argument straight to captureException, preserving its real message +
    // stack. Routing through logDownloadFailure (which takes a string message)
    // would instead have Logging.log synthesize a fresh Error rooted inside
    // itself, discarding the original stack — and the outermost caller
    // (UploadDropdownButton.tsx's .catch) further collapses the rethrown
    // exception via String(err) before its own Logging.error, so an unhandled
    // exception never reaches the browser's global handler either. Passing the
    // Error object here is the only place Sentry gets the actionable payload.
    //
    // Non-Error throws (someone `throw`ing a string, number, POJO) are wrapped
    // in a synthetic Error so Sentry still gets a captureException, at the cost
    // of losing whatever the caller threw beyond its String() form.
    Metrics.count(GOOGLE_DRIVE_DOWNLOAD_FAILURE_METRIC, 1, {reason: 'unhandled_error'});
    Logging.error(
      unhandledError instanceof Error
        ? unhandledError
        : new Error(`Non-Error thrown from downloadGoogleDriveFile: ${String(unhandledError)}`),
      {extra: {reason: 'unhandled_error', action: instructions.action}}
    );
    throw unhandledError;
  }
}

/**
 * Generator function that downloads Google drive files (either by a direct download or an export to a different filetype).
 * At each step, returns a DownloadResult, which contains either a File or an error object of type GoogleDriveIssue.
 *
 * @param documentInfos - list of information on the documents we want to either direct download or export
 * @param accessToken - the Google oAuth access token for this user
 * @param concurrencyLimit - the maximum number of download/export requests we want to wait for in parallel.
 *   We are capping this to ensure we don't have too many concurrent requests hitting Google at once (to prevent things like rate limiting).
 */
export async function* validateAndDownloadGoogleDriveFiles(
  documentInfos: Array<google.picker.DocumentObject>,
  accessToken: string,
  concurrencyLimit: number = CONCURRENCY_LIMIT
): AsyncGenerator<DownloadResult> {
  // Emit a total-invocations counter unconditionally so alerting can compute
  // picker_api_script_not_loaded / check_if_picker_api_script_is_loaded as a rate.
  Metrics.count(GOOGLE_DRIVE_CHECK_IF_PICKER_API_SCRIPT_IS_LOADED_METRIC, 1);

  if (typeof google === 'undefined' || !google.picker?.Document) {
    // Emit a dedicated picker_api_script_not_loaded metric (instead of the generic
    // download_failure counter) so its rate can be alerted on in isolation via
    // picker_api_script_not_loaded / check_if_picker_api_script_is_loaded. This is
    // a broken caller/sequencing precondition — validateAndDownloadGoogleDriveFiles
    // was invoked before the Google Picker API script loaded — so log at error
    // rather than warn.
    Metrics.count(GOOGLE_DRIVE_PICKER_API_SCRIPT_NOT_LOADED_METRIC, 1);
    Logging.error('GDrive download aborted — Picker API script not loaded');
    throw new GooglePickerAPIScriptNotLoadedError();
  }

  // Validate that concurrency limit is a positive number. If its not, fall back to default CONCURRENCY_LIMIT
  if (concurrencyLimit <= 0) {
    concurrencyLimit = CONCURRENCY_LIMIT;
  }

  const limiter = pLimit(concurrencyLimit);

  // Map of all of the promises to download/export files that we will kick off concurrently (limited by CONCURRENCY_LIMIT)
  const pendingFileRetrievalTasks = new Map<number, Promise<{taskId: number; value: DownloadResult}>>();

  let taskId: number = 0;

  // Loop through all documentInfos. Yield a DownloadResult with an error description if an invalid file is picked. Otherwise, construct
  // the promise to download or export the file, and add it to pendingFileRetrievalTasks.

  for (const documentInfo of documentInfos) {
    const fileName = documentInfo[google.picker.Document.NAME];
    const fileId = documentInfo[google.picker.Document.ID];
    const mimeType = documentInfo[google.picker.Document.MIME_TYPE];

    if (fileId == null) {
      logDownloadFailure(
        'GDrive download skipped — invalid picked file',
        {reason: 'invalid_file_missing_field'},
        {missingField: 'id'}
      );
      // Emit BEFORE the yield: an async-generator `.return()` (e.g. a consumer
      // breaking a for-await-of) resumes the suspended yield with an
      // early-return completion, so a post-yield emission would be skipped for
      // a file whose download_failure numerator was already emitted above.
      Metrics.count(GOOGLE_DRIVE_TOTAL_FILES_ATTEMPTED_FOR_DOWNLOAD_METRIC, 1);
      yield createInvalidFilePickedDownloadResult(
        '',
        fileName
          ? `File "${fileName}" is invalid and can't be uploaded. Try a different file`
          : "A selected file is invalid and can't be uploaded."
      );
      continue;
    }
    if (fileName == null) {
      logDownloadFailure(
        'GDrive download skipped — invalid picked file',
        {reason: 'invalid_file_missing_field'},
        {missingField: 'name'}
      );
      Metrics.count(GOOGLE_DRIVE_TOTAL_FILES_ATTEMPTED_FOR_DOWNLOAD_METRIC, 1);
      yield createInvalidFilePickedDownloadResult(
        fileId,
        `File with id "${fileId}" is invalid and can't be uploaded. Try a different file`
      );
      continue;
    }
    if (mimeType == null) {
      logDownloadFailure(
        'GDrive download skipped — invalid picked file',
        {reason: 'invalid_file_missing_field'},
        {missingField: 'mimeType'}
      );
      Metrics.count(GOOGLE_DRIVE_TOTAL_FILES_ATTEMPTED_FOR_DOWNLOAD_METRIC, 1);
      yield createInvalidFilePickedDownloadResult(
        fileId,
        `File "${fileName}" is invalid and can't be uploaded. Try a different file`
      );
      continue;
    }

    const inputFileInfo: InputFileInfo = {
      inputFileId: fileId,
      inputFileMimeType: mimeType,
      inputFileName: fileName,
    };

    let fileRetrievalTask;

    if (mimeType.startsWith(GOOGLE_MIME_TYPE_PREFIX)) {
      const behavior = getActionableGoogleMimeBehavior(mimeType);
      if (behavior === undefined) {
        const hasKnownGoogleMimeType = Object.hasOwn(GOOGLE_MIME_TYPES, mimeType);
        const missingField = hasKnownGoogleMimeType ? 'disallowed_type' : 'unrecognized_type';
        logDownloadFailure(
          'GDrive download skipped — invalid picked file',
          {reason: 'invalid_file_unrecognized_or_unallowed_type'},
          {missingField}
        );
        const userDescription = hasKnownGoogleMimeType
          ? `File "${fileName}" has a type that is not allowed for upload`
          : `File "${fileName}" has an unrecognized type for upload`;
        Metrics.count(GOOGLE_DRIVE_TOTAL_FILES_ATTEMPTED_FOR_DOWNLOAD_METRIC, 1);
        yield createInvalidFilePickedDownloadResult(fileId, userDescription);
        continue;
      }

      if (behavior.action === 'direct') {
        const outputFileInfo: DirectDownloadOutputFileInfo = {
          outputFileName: generateOutputFilenameForDirectDownloadOfGoogleMimeFiles(
            fileName,
            behavior.extension
          ),
          outputFileMimeType: behavior.outMime,
        };

        fileRetrievalTask = () =>
          downloadGoogleDriveFile(accessToken, {
            action: 'direct',
            inputFileInfo,
            outputFileInfo,
          });
      } else {
        // Action is export
        // Output file name should just be the current filename with the output file extension appended
        const outputFileInfo: ExportOutputFileInfo = {
          outputFileName: fileName + behavior.extension,
          outputFileMimeType: behavior.outMime,
          outputFileExtension: behavior.extension,
        };

        fileRetrievalTask = () =>
          downloadGoogleDriveFile(accessToken, {
            action: 'export',
            inputFileInfo,
            outputFileInfo,
          });
      }
    } else {
      // For all non-Google mime types, attempt to direct download them, keeping filename and mimetype the same
      const outputFileInfo: DirectDownloadOutputFileInfo = {
        outputFileName: fileName,
        outputFileMimeType: mimeType,
      };

      fileRetrievalTask = () =>
        downloadGoogleDriveFile(accessToken, {
          action: 'direct',
          inputFileInfo,
          outputFileInfo,
        });
    }

    const currentTaskId = taskId;
    const fileRetrievalTaskPromise = limiter(fileRetrievalTask).then((value) => ({
      taskId: currentTaskId,
      value,
    }));
    // If the generator is aborted during this loop, the drain loop below never runs and this
    // promise is never passed to Promise.race, so a rejection would go unhandled. Attaching a
    // no-op handler marks it handled without altering it: the map still holds the original
    // promise, so Promise.race observes the rejection and the drain loop still throws.
    fileRetrievalTaskPromise.catch(() => {});
    pendingFileRetrievalTasks.set(currentTaskId, fileRetrievalTaskPromise);
    // Emit at queue time, not when the result is yielded: p-limit runs this
    // task even if the generator is aborted before its result is delivered,
    // and downloadGoogleDriveFile can emit a download_failure numerator from
    // that background run.
    Metrics.count(GOOGLE_DRIVE_TOTAL_FILES_ATTEMPTED_FOR_DOWNLOAD_METRIC, 1);

    taskId += 1;
  }

  // Fire off all file retrieval tasks, yielding them in the order they finish.
  while (pendingFileRetrievalTasks.size > 0) {
    const {taskId, value} = await Promise.race(pendingFileRetrievalTasks.values());
    pendingFileRetrievalTasks.delete(taskId);
    yield value;
  }
}
